// Material UI v4 - Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Grid,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  Paper,
  Switch,
  FormControlLabel,
} from "@material-ui/core";

// Material UI v4 - Icons
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Receipt as ReceiptIcon,
} from "@material-ui/icons";

// Material UI v4 - Lab
import { LoadingButton } from "@material-ui/lab";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";

// React
import { useState, useEffect, useMemo, useCallback } from "react";

// RTK Query
import {
  useLazyBuscarFacturasQuery,
  useConciliarManualMutation,
  useObtenerCategoriasContablesQuery,
} from "src/services/conciliacionApi";

// Helpers
import {
  calcularRepartoCategorias,
  etiquetaNaturaleza,
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
  naturalezaDeLinea,
  NATURALEZA_LINEA,
  OPCIONES_NATURALEZA_LINEA,
  TOLERANCIA_REPARTO,
  validarMontosParciales,
} from "src/utils/conciliacionHelpers";

// Snackbar
import { useSnackbar } from "src/components/snackbar";

// Styles
import { makeStyles } from "@material-ui/styles";
import { formatearFecha, formatearFechaDisplay } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  filtrosHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    cursor: "pointer",
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    "&:hover": {
      backgroundColor: theme.palette.grey[200],
    },
  },
  filtrosContent: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  resumenSeleccion: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
}));

/**
 * Modal para conciliación manual con búsqueda de facturas
 *
 * @param {boolean} open - Controla si el modal está abierto
 * @param {function} onClose - Callback al cerrar el modal
 * @param {Object} movimiento - Objeto del movimiento bancario a conciliar
 * @param {function} onSuccess - Callback después de conciliación exitosa
 */
export default function ConciliacionManualModal({
  open,
  onClose,
  movimiento,
  onSuccess,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // ============================================
  // ESTADOS DE FILTROS
  // ============================================
  const [filtros, setFiltros] = useState({
    rut_cliente: "",
    fecha_desde: null,
    fecha_hasta: null,
    monto_min: "",
    monto_max: "",
    folio: "",
    estado: "", // FACTURADO | ABONO
    tipo: "", // PLAN | TAREA
  });

  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  // ============================================
  // ESTADOS DE SELECCIÓN Y CONCILIACIÓN
  // ============================================

  // Facturas seleccionadas para conciliar
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);

  // Montos asignados a cada factura (para conciliación parcial)
  const [montosAsignados, setMontosAsignados] = useState({});

  // Observaciones de la conciliación
  const [observaciones, setObservaciones] = useState("");

  // Estado de procesamiento
  const [procesando, setProcesando] = useState(false);

  // Categorizaciones contables: array de líneas de ajuste contable que pueden
  // coexistir con facturas y con abono por remanente. Cada línea:
  //   { uiId, categoria_contable_id, monto, naturaleza, observaciones }
  // `naturaleza` declara si el monto ingresó junto con el movimiento (ADITIVA)
  // o si se retuvo antes de depositarlo (SUSTRACTIVA). Es propiedad de la
  // línea y no de la categoría: dos líneas de la misma categoría pueden llevar
  // signos distintos dentro del mismo movimiento.
  // `uiId` es un identificador local (React key / tracking); no se envía al backend.
  const [categoriasAjuste, setCategoriasAjuste] = useState([]);
  const [usuarioDecidio, setUsuarioDecidio] = useState(false);
  const [modoRetencionCompleta, setModoRetencionCompleta] = useState(false);

  // Helpers de manipulación del array de categorías
  const agregarCategoria = useCallback(
    (montoPrefill = 0, naturaleza = NATURALEZA_LINEA.ADITIVA) => {
      setCategoriasAjuste((prev) => [
        ...prev,
        {
          uiId: `cat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          categoria_contable_id: "",
          monto: montoPrefill,
          naturaleza,
          observaciones: "",
        },
      ]);
    },
    [],
  );

  const eliminarCategoria = useCallback((uiId) => {
    setCategoriasAjuste((prev) => prev.filter((c) => c.uiId !== uiId));
  }, []);

  const actualizarCategoria = useCallback((uiId, campo, valor) => {
    setCategoriasAjuste((prev) =>
      prev.map((c) => (c.uiId === uiId ? { ...c, [campo]: valor } : c)),
    );
  }, []);
  // ============================================
  // RTK QUERY
  // ============================================

  // Búsqueda de facturas (lazy para ejecutar manualmente)
  const [
    buscarFacturas,
    {
      data: resultadosFacturas,
      isLoading: isLoadingFacturas,
      isFetching: isFetchingFacturas,
      isError: isErrorFacturas,
    },
  ] = useLazyBuscarFacturasQuery();

  // Mutation para conciliar manualmente
  const [conciliarManual, { isLoading: isConciliando }] =
    useConciliarManualMutation();

  // Cargar categorías contables activas para ajuste de comisión
  const { data: categoriasData } = useObtenerCategoriasContablesQuery({
    activo: "1",
  });

  // ============================================
  // EFFECTS
  // ============================================

  // Buscar automáticamente al abrir el modal con filtros sugeridos
  useEffect(() => {
    if (open && movimiento) {
      // Pre-llenar filtros sugeridos basados en el movimiento
      const filtrosSugeridos = {
        rut_cliente: movimiento.rut_ordenante || "",
        fecha_desde: null,
        fecha_hasta: null,
        monto_min: 0, // 80% del monto
        monto_max: Math.ceil(movimiento.monto * 1.2), // 120% del monto
        folio: "",
        estado: "",
        tipo: "",
      };

      setFiltros(filtrosSugeridos);

      // Ejecutar búsqueda automática
      const params = {};
      if (filtrosSugeridos.rut_cliente)
        params.rut_cliente = filtrosSugeridos.rut_cliente;
      if (filtrosSugeridos.monto_min)
        params.monto_min = filtrosSugeridos.monto_min;
      if (filtrosSugeridos.monto_max)
        params.monto_max = filtrosSugeridos.monto_max;
      if (filtrosSugeridos.estado) params.estado = filtrosSugeridos.estado;

      buscarFacturas(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, movimiento]);

  // Distribución automática cuando el usuario decide "pago insuficiente"
  // (reparte el movimiento entre facturas ordenadas por folio ASC, igual que
  // el backend). Solo aplica cuando NO hay categorías contables declaradas.
  useEffect(() => {
    if (
      !usuarioDecidio ||
      categoriasAjuste.length > 0 ||
      facturasSeleccionadas.length === 0 ||
      !movimiento
    ) {
      return;
    }

    const sumaTotal = Object.values(montosAsignados).reduce(
      (sum, monto) => sum + parseFloat(monto || 0),
      0,
    );

    const hayInsuficiencia = sumaTotal > movimiento.monto;
    if (!hayInsuficiencia) return;

    const facturasOrdenadas = [...facturasSeleccionadas].sort(
      (a, b) => parseInt(a.folio) - parseInt(b.folio),
    );

    let montoRestante = movimiento.monto;
    const nuevosMontos = {};

    facturasOrdenadas.forEach((factura) => {
      const facturaKey = `${factura.tipo}-${factura.id}`;
      const deudaFactura = factura.deuda_restante;

      if (montoRestante >= deudaFactura) {
        nuevosMontos[facturaKey] = deudaFactura;
        montoRestante -= deudaFactura;
      } else if (montoRestante > 0) {
        nuevosMontos[facturaKey] = montoRestante;
        montoRestante = 0;
      } else {
        nuevosMontos[facturaKey] = 0;
      }
    });

    setMontosAsignados(nuevosMontos);

    enqueueSnackbar(
      "Montos ajustados automáticamente según distribución por antigüedad",
      { variant: "info" },
    );
  }, [
    usuarioDecidio,
    categoriasAjuste.length,
    facturasSeleccionadas,
    movimiento,
    montosAsignados,
    enqueueSnackbar,
  ]);

  // ============================================
  // FUNCIONES DE BÚSQUEDA
  // ============================================

  // Ejecutar búsqueda con los filtros actuales
  const handleBuscar = () => {
    // Construir params de búsqueda (solo incluir filtros con valores)
    const params = {};

    if (filtros.rut_cliente) params.rut_cliente = filtros.rut_cliente;
    if (filtros.fecha_desde)
      params.fecha_desde = formatearFecha(filtros.fecha_desde, "YYYY-MM-DD");
    if (filtros.fecha_hasta)
      params.fecha_hasta = formatearFecha(filtros.fecha_hasta, "YYYY-MM-DD");
    if (filtros.monto_min) params.monto_min = parseFloat(filtros.monto_min);
    if (filtros.monto_max) params.monto_max = parseFloat(filtros.monto_max);
    if (filtros.folio) params.folio = filtros.folio;
    if (filtros.estado) params.estado = filtros.estado;
    if (filtros.tipo) params.tipo = filtros.tipo;

    // Ejecutar búsqueda
    buscarFacturas(params);
  };

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltros({
      rut_cliente: "",
      fecha_desde: null,
      fecha_hasta: null,
      monto_min: "",
      monto_max: "",
      folio: "",
      estado: "",
      tipo: "",
    });

    // Opcional: Mostrar mensaje
    enqueueSnackbar("Filtros limpiados", { variant: "info" });
  };
  // Cambiar valor de filtro
  const handleChangeFiltro = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // ============================================
  // FUNCIONES DE SELECCIÓN
  // ============================================

  // Seleccionar/deseleccionar todas las facturas
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Seleccionar todas las facturas
      const todasLasFacturas = resultadosFacturas.data.map((factura) => ({
        tipo: factura.tipo,
        id: factura.id,
        folio: factura.folio,
        monto: factura.monto,
        monto_pagado: factura.monto_pagado,
        deuda_restante: factura.deuda_restante,
        cliente_nombre: factura.nombre_cliente,
        rut_cliente: factura.rut_cliente,
        fecha_emision: factura.fecha_emision,
        estado: factura.estado,
        sucursal: factura.sucursal,
      }));
      setFacturasSeleccionadas(todasLasFacturas);

      // Inicializar montos asignados con la deuda restante de cada factura
      const nuevosMontos = {};
      todasLasFacturas.forEach((factura) => {
        nuevosMontos[`${factura.tipo}-${factura.id}`] = factura.deuda_restante;
      });
      setMontosAsignados(nuevosMontos);
    } else {
      // Deseleccionar todas
      setFacturasSeleccionadas([]);
      setMontosAsignados({});
    }
  };

  // Seleccionar/deseleccionar una factura individual
  const handleSelectFactura = (factura) => {
    const facturaKey = `${factura.tipo}-${factura.id}`;
    const yaEstaSeleccionada = facturasSeleccionadas.some(
      (f) => f.tipo === factura.tipo && f.id === factura.id,
    );

    if (yaEstaSeleccionada) {
      // Deseleccionar
      setFacturasSeleccionadas((prev) =>
        prev.filter((f) => !(f.tipo === factura.tipo && f.id === factura.id)),
      );

      // Eliminar monto asignado
      setMontosAsignados((prev) => {
        const { [facturaKey]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      // Seleccionar
      const nuevaFactura = {
        tipo: factura.tipo,
        id: factura.id,
        folio: factura.folio,
        monto: factura.monto,
        monto_pagado: factura.monto_pagado,
        deuda_restante: factura.deuda_restante,
        cliente_nombre: factura.nombre_cliente,
        rut_cliente: factura.rut_cliente,
        fecha_emision: factura.fecha_emision,
        estado: factura.estado,
        sucursal: factura.sucursal,
      };
      setFacturasSeleccionadas((prev) => [...prev, nuevaFactura]);

      // Inicializar monto asignado con la deuda restante
      setMontosAsignados((prev) => ({
        ...prev,
        [facturaKey]: factura.deuda_restante,
      }));
    }
  };

  // Verificar si una factura está seleccionada
  const isFacturaSeleccionada = (factura) => {
    return facturasSeleccionadas.some(
      (f) => f.tipo === factura.tipo && f.id === factura.id,
    );
  };

  // Cambiar el monto asignado a una factura específica
  const handleChangeMonto = (factura, nuevoMonto) => {
    const facturaKey = `${factura.tipo}-${factura.id}`;
    const montoNumerico = parseFloat(nuevoMonto) || 0;

    // Validar que no exceda la deuda restante
    if (montoNumerico > factura.deuda_restante) {
      enqueueSnackbar(
        `El monto no puede exceder la deuda restante (${formatearMonto(
          factura.deuda_restante,
        )})`,
        { variant: "warning" },
      );
      return;
    }

    // Validar que no sea negativo
    if (montoNumerico < 0) {
      enqueueSnackbar("El monto no puede ser negativo", { variant: "warning" });
      return;
    }

    setMontosAsignados((prev) => ({
      ...prev,
      [facturaKey]: montoNumerico,
    }));
  };

  // ============================================
  // FUNCIÓN DE CONCILIACIÓN
  // ============================================

  // Ejecutar conciliación manual
  const handleConciliar = async () => {
    // Validar que exista el movimiento
    if (!movimiento) {
      enqueueSnackbar("Error: Movimiento no encontrado", { variant: "error" });
      return;
    }

    // Sin retención completa hace falta una factura: una línea de categoría que
    // quedó sin factura ya no se ve en pantalla y no puede repartir nada.
    if (!modoRetencionCompleta && facturasSeleccionadas.length === 0) {
      enqueueSnackbar("Debe seleccionar al menos una factura", {
        variant: "warning",
      });
      return;
    }

    // Validar cada línea de categoría
    for (const linea of categoriasAjuste) {
      if (!linea.categoria_contable_id) {
        enqueueSnackbar(
          "Todas las categorías deben tener una categoría contable seleccionada",
          { variant: "error" },
        );
        return;
      }
      if (!linea.monto || parseFloat(linea.monto) <= 0) {
        enqueueSnackbar("Todas las categorías deben tener monto mayor a 0", {
          variant: "error",
        });
        return;
      }
    }

    // El cuadre del reparto no se valida acá. La política vive entera en el
    // backend, que es quien conoce la fórmula y quien rechaza con su motivo:
    // repetirla en el modal deja dos autoridades sobre el mismo número y una
    // de las dos queda atrás sin que nadie lo note.
    setProcesando(true);

    try {
      // Preparar datos para el endpoint
      const payload = {
        movimiento_id: movimiento.id,
        facturas: modoRetencionCompleta
          ? [] // ← Array vacío para retención completa
          : facturasSeleccionadas.map((factura) => ({
              tipo: factura.tipo,
              id: factura.id,
              monto_asignado: montosAsignados[`${factura.tipo}-${factura.id}`],
            })),
        categorias_ajuste: categoriasAjuste.map((linea) => ({
          categoria_contable_id: parseInt(linea.categoria_contable_id),
          monto: parseFloat(linea.monto),
          naturaleza: naturalezaDeLinea(linea),
          ...(linea.observaciones?.trim()
            ? { observaciones: linea.observaciones.trim() }
            : {}),
        })),
      };

      // Agregar observaciones generales si existen
      if (observaciones.trim()) {
        payload.observaciones = observaciones.trim();
      }

      // Ejecutar mutation
      await conciliarManual(payload).unwrap();

      // Éxito
      enqueueSnackbar(
        modoRetencionCompleta
          ? `Retención completa registrada con éxito. Monto: ${formatearMonto(
              movimiento.monto,
            )}`
          : `Conciliación realizada con éxito. ${
              facturasSeleccionadas.length
            } ${
              facturasSeleccionadas.length === 1
                ? "factura conciliada"
                : "facturas conciliadas"
            }`,
        { variant: "success" },
      );

      // Limpiar estados ANTES de cerrar
      limpiarYCerrar();

      // Callback de éxito DESPUÉS de limpiar
      if (onSuccess && typeof onSuccess === "function") {
        // Construir detalle para agregar a recienConciliados
        const detalleConciliado = {
          movimiento_id: movimiento.id,
          status: "MANUAL_CONCILIADO",
          score: 0, // No hay score en conciliación manual
          nivel: "MANUAL",
          movimiento: movimiento,
          facturas: facturasSeleccionadas,
          conciliadoEn: new Date().toISOString(),
        };

        onSuccess(detalleConciliado);
      }
    } catch (error) {
      console.error("Error al conciliar manualmente:", error);
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Error al realizar la conciliación manual",
        { variant: "error" },
      );
    } finally {
      setProcesando(false);
    }
  };

  // Valida que todas las líneas de categorización estén completas
  const categoriasCompletas = useMemo(() => {
    if (categoriasAjuste.length === 0) return true;
    return categoriasAjuste.every(
      (c) =>
        c.categoria_contable_id &&
        parseFloat(c.monto) > 0 &&
        !Number.isNaN(parseFloat(c.monto)),
    );
  }, [categoriasAjuste]);

  // ============================================
  // FUNCIÓN DE DISTRIBUCIÓN AUTOMÁTICA
  // ============================================

  /**
   * Cuando el usuario confirma que la diferencia negativa (sumaFacturas > monto)
   * corresponde a un ajuste contable, agregamos una línea de categoría
   * pre-llenada con el monto de la diferencia para que complete la categoría.
   */
  const handleDecisionUsuario = useCallback(
    (esAjusteContable) => {
      setUsuarioDecidio(true);
      if (esAjusteContable) {
        const sumaFacturas = Object.values(montosAsignados).reduce(
          (sum, monto) => sum + parseFloat(monto || 0),
          0,
        );
        const diferencia = Math.abs(movimiento.monto - sumaFacturas);
        // El signo sale de la misma comparación de la que sale el monto. Cuando
        // las facturas superan al movimiento falta plata, y eso solo se explica
        // porque algo se retuvo antes del depósito: con esa línea marcada como
        // ingreso el reparto queda en el doble de la diferencia y el backend la
        // rechaza siempre, así que sustractiva es el único signo que puede
        // cuadrar. Es un valor inicial que el operador ve y puede cambiar; el
        // cálculo sigue leyendo lo que la línea declara, no de dónde salió.
        const naturaleza =
          sumaFacturas > movimiento.monto
            ? NATURALEZA_LINEA.SUSTRACTIVA
            : NATURALEZA_LINEA.ADITIVA;
        if (diferencia > 1 && categoriasAjuste.length === 0) {
          agregarCategoria(diferencia, naturaleza);
        }
      }
    },
    [montosAsignados, movimiento, categoriasAjuste.length, agregarCategoria],
  );

  // Determina si el botón de conciliar debe estar deshabilitado
  const botonConciliarDeshabilitado = useMemo(() => {
    if (!movimiento) return true;

    // ================================================
    // CASO ESPECIAL: RETENCIÓN COMPLETA (sin facturas)
    // ================================================
    if (modoRetencionCompleta) {
      if (categoriasAjuste.length === 0) return true;
      if (!categoriasCompletas) return true;
      return false;
    }

    // ================================================
    // FLUJO NORMAL
    // ================================================

    // Fuera del modo de retención completa hace falta al menos una factura: las
    // líneas de categoría reparten el monto de las facturas, y el panel que las
    // muestra no se pinta sin ellas. Una línea que sobreviva a deseleccionar la
    // última factura queda invisible en pantalla pero sigue viajando en el envío,
    // y una sustractiva ahí genera un abono mayor que el propio depósito.
    if (facturasSeleccionadas.length === 0) {
      return true;
    }

    // Todas las categorías deben estar completas
    if (!categoriasCompletas) return true;

    const sumaFacturas = Object.values(montosAsignados).reduce(
      (sum, monto) => sum + parseFloat(monto || 0),
      0,
    );

    // Con líneas de categoría el operador declara un desglose y el backend
    // decide si cuadra; acá basta con que cada línea esté completa. La rama de
    // abajo queda reservada al caso sin líneas, donde una diferencia sigue
    // significando pago insuficiente y hay que repartir entre las facturas.
    if (categoriasAjuste.length > 0) return false;

    // Sin categorías: misma lógica antigua
    const diferencia = Math.abs(movimiento.monto - sumaFacturas);
    const hayDiferencia = diferencia > 1;
    if (hayDiferencia) {
      // Espera a que el usuario decida qué hacer con la diferencia
      if (!usuarioDecidio) return true;
      // Si decidió "no es ajuste contable", el backend distribuye o genera abono
      return false;
    }

    const validacion = validarMontosParciales(
      facturasSeleccionadas.map((f) => ({
        ...f,
        monto_asignado: montosAsignados[`${f.tipo}-${f.id}`],
      })),
      movimiento.monto,
    );
    return !validacion.valido;
  }, [
    facturasSeleccionadas,
    movimiento,
    montosAsignados,
    usuarioDecidio,
    modoRetencionCompleta,
    categoriasAjuste.length,
    categoriasCompletas,
  ]);

  // Cuenta cuántos filtros tienen valores
  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.rut_cliente) count++;
    if (filtros.fecha_desde) count++;
    if (filtros.fecha_hasta) count++;
    if (filtros.monto_min) count++;
    if (filtros.monto_max) count++;
    if (filtros.folio) count++;
    if (filtros.estado) count++;
    if (filtros.tipo) count++;
    return count;
  };

  // ============================================
  // FUNCIONES DE CONTROL
  // ============================================

  // Cerrar modal y limpiar estados
  // Cerrar modal y limpiar estados
  const handleClose = () => {
    if (procesando || isConciliando) return;

    // Si hay facturas seleccionadas, confirmar antes de cerrar
    if (facturasSeleccionadas.length > 0) {
      // if (
      //   window.confirm(
      //     "¿Está seguro de cerrar? Se perderán las facturas seleccionadas."
      //   )
      // ) {
      //   limpiarYCerrar();
      // }
      limpiarYCerrar();
    } else {
      limpiarYCerrar();
    }
  };

  // Limpiar todos los estados y cerrar
  const limpiarYCerrar = () => {
    setFiltros({
      rut_cliente: "",
      fecha_desde: null,
      fecha_hasta: null,
      monto_min: "",
      monto_max: "",
      folio: "",
      estado: "",
      tipo: "",
    });

    setFacturasSeleccionadas([]);
    setMontosAsignados({});
    setObservaciones("");
    setMostrarFiltros(true);

    setCategoriasAjuste([]);
    setUsuarioDecidio(false);
    setModoRetencionCompleta(false);

    onClose();
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  // Renderiza la información del movimiento a conciliar
  const renderInfoMovimiento = () => {
    if (!movimiento) return null;

    const bancoConfig = getBancoConfig(movimiento.banco);

    return (
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          MOVIMIENTO A CONCILIAR
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="caption" color="textSecondary">
                  Fecha
                </Typography>
                <Typography variant="body2">
                  {formatearFechaDisplay(movimiento.fecha_mov)}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Typography variant="caption" color="textSecondary">
                  Banco
                </Typography>
                <Box>
                  <Chip
                    label={bancoConfig.label}
                    size="small"
                    style={{
                      backgroundColor: bancoConfig.bgColor,
                      color: bancoConfig.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={2}>
                <Typography variant="caption" color="textSecondary">
                  Monto
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 700,
                    color:
                      movimiento.tipo === "INGRESO" ? "#4CAF50" : "#F44336",
                    fontSize: "1.1rem",
                  }}
                >
                  {formatearMonto(movimiento.monto)}
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography variant="caption" color="textSecondary">
                  RUT Ordenante
                </Typography>
                <Typography variant="body2">
                  {formatearRutDisplay(movimiento.rut_ordenante)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Glosa
                </Typography>
                <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                  {movimiento.glosa}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Renderiza la tabla de resultados
  const renderTablaResultados = () => {
    if (!resultadosFacturas?.data || resultadosFacturas.data.length === 0)
      return null;

    const facturas = resultadosFacturas.data;
    const todasSeleccionadas =
      facturasSeleccionadas.length === facturas.length && facturas.length > 0;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: "#F5F5F5" }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={todasSeleccionadas}
                  indeterminate={
                    facturasSeleccionadas.length > 0 &&
                    facturasSeleccionadas.length < facturas.length
                  }
                  onChange={handleSelectAll}
                  color="primary"
                />
              </TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Folio</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>RUT</TableCell>
              <TableCell>Fecha Emisión</TableCell>
              <TableCell align="right">Monto Total</TableCell>
              <TableCell align="right">Deuda</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facturas.map((factura, index) => {
              const isSelected = isFacturaSeleccionada(factura);

              return (
                <TableRow
                  key={`${factura.tipo}-${factura.id}`}
                  hover
                  onClick={() => handleSelectFactura(factura)}
                  selected={isSelected}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#E3F2FD" : "inherit",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isSelected} color="primary" />
                  </TableCell>

                  {/* Tipo */}
                  <TableCell>
                    <Chip
                      label={factura.tipo}
                      size="small"
                      style={{
                        backgroundColor:
                          factura.tipo === "PLAN" ? "#E3F2FD" : "#FFF3E0",
                        color: factura.tipo === "PLAN" ? "#1976D2" : "#F57C00",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>

                  {/* Folio */}
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      #{factura.folio}
                    </Typography>
                  </TableCell>

                  {/* Cliente */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      style={{ maxWidth: 200 }}
                    >
                      {factura.nombre_cliente || "Sin nombre"}
                    </Typography>
                    {factura.sucursal && (
                      <Typography variant="caption" color="textSecondary">
                        {factura.sucursal}
                      </Typography>
                    )}
                  </TableCell>

                  {/* RUT */}
                  <TableCell>
                    <Typography variant="body2">
                      {factura.rut_cliente
                        ? formatearRutDisplay(factura.rut_cliente)
                        : "Sin RUT"}
                    </Typography>
                  </TableCell>

                  {/* Fecha Emisión */}
                  <TableCell>
                    <Typography variant="body2">
                      {formatearFechaDisplay(factura.fecha_emision)}
                    </Typography>
                  </TableCell>

                  {/* Monto Total */}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      {formatearMonto(factura.monto)}
                    </Typography>
                  </TableCell>

                  {/* Deuda Restante */}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 700,
                        color: factura.monto_pagado > 0 ? "#F57C00" : "#2E7D32",
                      }}
                    >
                      {formatearMonto(factura.deuda_restante)}
                    </Typography>
                    {factura.monto_pagado > 0 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Pagado: {formatearMonto(factura.monto_pagado)}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Estado */}
                  <TableCell>
                    <Chip
                      label={factura.estado}
                      size="small"
                      style={{
                        backgroundColor:
                          factura.estado === "FACTURADO"
                            ? "#FFF3E0"
                            : "#E8F5E9",
                        color:
                          factura.estado === "FACTURADO"
                            ? "#F57C00"
                            : "#2E7D32",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Detecta si la glosa sugiere que es un pago con comisión
  const detectarPosibleComision = (glosa) => {
    if (!glosa) return { posible: false, palabraDetectada: null };

    const palabrasClave = ["HAULMER", "WEBPAY", "TRANSBANK", "PNOL", "POS"];
    const glosaUpper = glosa.toUpperCase();

    for (const palabra of palabrasClave) {
      if (glosaUpper.includes(palabra)) {
        return {
          posible: true,
          palabraDetectada: palabra,
        };
      }
    }

    return { posible: false, palabraDetectada: null };
  };

  // Renderiza el resumen de facturas seleccionadas
  const renderResumenSeleccion = () => {
    if (!movimiento) return null;
    if (facturasSeleccionadas.length === 0 && !modoRetencionCompleta)
      return null;

    // Detectar si la glosa sugiere una comisión
    const deteccionComision = movimiento?.glosa
      ? detectarPosibleComision(movimiento.glosa)
      : { posible: false, palabraDetectada: null };

    // Calcular suma total de montos asignados
    const sumaTotal = Object.values(montosAsignados).reduce(
      (sum, monto) => sum + parseFloat(monto || 0),
      0,
    );

    // Calcular diferencia con el movimiento
    const diferencia = movimiento.monto - sumaTotal;

    const esDiferenciaComision = diferencia < 0; // suma > monto
    const esDiferenciaRetencion = diferencia > 0; // monto > suma
    const hayDiferencia = Math.abs(diferencia) > 1;

    // Validar montos
    const validacion = validarMontosParciales(
      facturasSeleccionadas.map((f) => ({
        ...f,
        monto_asignado: montosAsignados[`${f.tipo}-${f.id}`],
      })),
      movimiento.monto,
    );

    return (
      <Box className={classes.resumenSeleccion}>
        {!modoRetencionCompleta && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              RESUMEN DE SELECCIÓN
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Facturas seleccionadas
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {facturasSeleccionadas.length}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Monto del movimiento
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {formatearMonto(movimiento.monto)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Suma de facturas
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: 700,
                    color: validacion.valido ? "#2E7D32" : "#D32F2F",
                  }}
                >
                  {formatearMonto(sumaTotal)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Diferencia
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: 700,
                    color: diferencia === 0 ? "#2E7D32" : "#F57C00",
                  }}
                >
                  {formatearMonto(Math.abs(diferencia))}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        {/* Alert para CUALQUIER diferencia (comisión o retención) */}
        {hayDiferencia &&
          !modoRetencionCompleta &&
          categoriasAjuste.length === 0 && (
            <Alert
              severity={esDiferenciaComision ? "warning" : "info"}
              style={{ marginTop: 16 }}
            >
              <Typography variant="body2" gutterBottom>
                <strong>
                  {esDiferenciaComision
                    ? "⚠️ Diferencia detectada:"
                    : "ℹ️ Excedente detectado:"}
                </strong>{" "}
                {esDiferenciaComision ? (
                  <>
                    La suma de facturas ({formatearMonto(sumaTotal)}) excede el
                    monto del movimiento ({formatearMonto(movimiento.monto)}) en{" "}
                    {formatearMonto(Math.abs(diferencia))}.
                  </>
                ) : (
                  <>
                    El monto del movimiento ({formatearMonto(movimiento.monto)})
                    excede la suma de facturas ({formatearMonto(sumaTotal)}) en{" "}
                    {formatearMonto(Math.abs(diferencia))}.
                  </>
                )}
              </Typography>

              {/* Hint visual si se detectó palabra clave */}
              {deteccionComision.posible && esDiferenciaComision && (
                <Typography
                  variant="caption"
                  display="block"
                  style={{ marginTop: 8, fontStyle: "italic" }}
                >
                  💡 Detectamos "{deteccionComision.palabraDetectada}" en la
                  glosa. ¿Esta diferencia corresponde a una comisión?
                </Typography>
              )}

              {/* Hint para retenciones */}
              {esDiferenciaRetencion && (
                <Typography
                  variant="caption"
                  display="block"
                  style={{ marginTop: 8, fontStyle: "italic" }}
                >
                  💡 ¿Este excedente corresponde a retenciones del cliente
                  (contribuciones, impuestos, obligaciones)?
                </Typography>
              )}

              {/* Botones de decisión */}
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>
                  <strong>¿Cómo desea proceder?</strong>
                </Typography>

                <Box mt={1} display="flex" gap={1} flexDirection="column">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleDecisionUsuario(true)}
                    startIcon={<ReceiptIcon />}
                  >
                    Sí, es un ajuste contable (comisión/retención)
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => handleDecisionUsuario(false)}
                    startIcon={<TimelineIcon />}
                  >
                    No,{" "}
                    {diferencia < 0 ? "es pago insuficiente" : "generar abono"}
                  </Button>
                </Box>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginTop: 8 }}
                >
                  {diferencia < 0 ? (
                    <>
                      <strong>Pago insuficiente:</strong> Las facturas se
                      pagarán por antigüedad (folio ascendente). Las últimas
                      facturas recibirán pagos parciales o no se procesarán.
                    </>
                  ) : (
                    <>
                      <strong>Exceso:</strong> Se generará un abono pendiente de{" "}
                      {formatearMonto(Math.abs(diferencia))} que podrá aplicarse
                      posteriormente a otras facturas.
                    </>
                  )}
                </Typography>
              </Box>
            </Alert>
          )}

        {validacion.valido &&
          diferencia > 0 &&
          !modoRetencionCompleta &&
          categoriasAjuste.length === 0 && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              <Typography variant="body2">
                <strong>Aviso:</strong> Quedarán {formatearMonto(diferencia)}{" "}
                sin asignar. Esta será una conciliación parcial.
              </Typography>
            </Alert>
          )}

        {/* Panel de categorizaciones contables (N líneas) */}
        {(categoriasAjuste.length > 0 || modoRetencionCompleta) && (
          <Box
            mt={2}
            p={2}
            style={{
              backgroundColor: "#FFF9E6",
              borderRadius: 4,
              border: "1px solid #FFE082",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {modoRetencionCompleta
                  ? "🏦 Retención Completa (Sin Facturas)"
                  : "📊 Categorizaciones Contables"}
              </Typography>
              {!modoRetencionCompleta && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => agregarCategoria()}
                >
                  + Agregar categoría
                </Button>
              )}
            </Box>

            {categoriasAjuste.map((linea, idx) => (
              <Card
                key={linea.uiId}
                variant="outlined"
                style={{ marginBottom: 8, padding: 12 }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel id={`cat-label-${linea.uiId}`}>
                        Categoría contable *
                      </InputLabel>
                      <Select
                        labelId={`cat-label-${linea.uiId}`}
                        value={linea.categoria_contable_id}
                        onChange={(e) =>
                          actualizarCategoria(
                            linea.uiId,
                            "categoria_contable_id",
                            e.target.value,
                          )
                        }
                        label="Categoría contable *"
                      >
                        <MenuItem value="" disabled>
                          <em>Seleccione una categoría</em>
                        </MenuItem>
                        {categoriasData?.data?.map((categoria) => (
                          <MenuItem key={categoria.id} value={categoria.id}>
                            {categoria.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Monto *"
                      type="number"
                      value={linea.monto}
                      onChange={(e) =>
                        actualizarCategoria(
                          linea.uiId,
                          "monto",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Typography
                            variant="body2"
                            style={{ marginRight: 4 }}
                          >
                            $
                          </Typography>
                        ),
                      }}
                      inputProps={{
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.target.blur(),
                      }}
                      disabled={modoRetencionCompleta && idx === 0}
                    />
                  </Grid>

                  {/* El signo del monto vive acá y no en el importe: el mismo
                      concepto contable puede llegar con el pago en un
                      movimiento y estar retenido en otro. En retención
                      completa la línea explica el movimiento entero, así que
                      marcarla como retenida pediría el doble del depósito y
                      dejaría un abono que nadie recibió. */}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel id={`naturaleza-label-${linea.uiId}`}>
                        Este monto
                      </InputLabel>
                      <Select
                        labelId={`naturaleza-label-${linea.uiId}`}
                        value={naturalezaDeLinea(linea)}
                        onChange={(e) =>
                          actualizarCategoria(
                            linea.uiId,
                            "naturaleza",
                            e.target.value,
                          )
                        }
                        label="Este monto"
                        disabled={modoRetencionCompleta}
                      >
                        {OPCIONES_NATURALEZA_LINEA.map((opcion) => (
                          <MenuItem key={opcion.value} value={opcion.value}>
                            {opcion.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={1}>
                    <Box display="flex" justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => eliminarCategoria(linea.uiId)}
                        disabled={modoRetencionCompleta}
                        aria-label="Eliminar categoría"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Observaciones (opcional)"
                      multiline
                      rows={2}
                      value={linea.observaciones}
                      onChange={(e) =>
                        actualizarCategoria(
                          linea.uiId,
                          "observaciones",
                          e.target.value,
                        )
                      }
                      variant="outlined"
                      placeholder="Ej: Reembolso Previred abril 2026"
                      inputProps={{ maxLength: 500 }}
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}

            {/* Desglose del reparto. Reconstruye primero el dinero que hay que
                explicar --lo depositado más lo que se retuvo antes de
                depositarlo-- para que el único signo + quede aislado en su
                sección y de ahí hacia abajo todo reste. Una línea rotulada
                "retenido antes del depósito" que apareciera sumando en medio
                de la cuenta se lee como un error de captura. */}
            {(() => {
              const reparto = calcularRepartoCategorias({
                montoMovimiento: movimiento.monto,
                sumaFacturas: sumaTotal,
                lineas: categoriasAjuste,
              });

              const nombreCategoria = (linea) =>
                categoriasData?.data?.find(
                  (c) => String(c.id) === String(linea.categoria_contable_id),
                )?.nombre || "sin categoría";

              // Con una sola línea el nombre de la categoría dice más que el
              // conteo; con varias, el conteo evita una fila por cada una.
              const detalleGrupo = (grupo) =>
                grupo.length === 1
                  ? nombreCategoria(grupo[0])
                  : `${grupo.length} líneas`;

              // Una fila cuyo monto es 0 no aporta nada al reparto y solo
              // alarga la lectura.
              const filasFacturas = facturasSeleccionadas
                .map((factura) => ({
                  clave: `${factura.tipo}-${factura.id}`,
                  etiqueta: `Factura ${factura.folio}`,
                  monto:
                    parseFloat(
                      montosAsignados[`${factura.tipo}-${factura.id}`],
                    ) || 0,
                }))
                .filter((fila) => fila.monto > 0);

              const hayRetencion = reparto.sumaSustractivas > 0;
              const remanente = reparto.remanente;

              const filaDesglose = (clave, signo, etiqueta, monto, color) => (
                <Box key={clave} display="flex" alignItems="baseline" mb={0.5}>
                  <Typography
                    variant="body2"
                    style={{
                      width: 16,
                      flexShrink: 0,
                      fontWeight: 700,
                      color: signo === "+" ? "#2E7D32" : "#616161",
                    }}
                  >
                    {signo}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ flexGrow: 1, color: color || "inherit" }}
                  >
                    {etiqueta}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      marginLeft: 16,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      color: color || "inherit",
                    }}
                  >
                    {monto === null ? "" : formatearMonto(monto)}
                  </Typography>
                </Box>
              );

              let cierre;
              if (remanente > TOLERANCIA_REPARTO) {
                cierre = filaDesglose(
                  "cierre",
                  "=",
                  "Remanente, queda como abono",
                  remanente,
                  "#1976D2",
                );
              } else if (remanente < -TOLERANCIA_REPARTO) {
                cierre = filaDesglose(
                  "cierre",
                  "=",
                  "Lo repartido excede el total a repartir",
                  Math.abs(remanente),
                  "#D32F2F",
                );
              } else {
                cierre = filaDesglose(
                  "cierre",
                  "=",
                  "Cuadra exacto, sin remanente",
                  null,
                  "#2E7D32",
                );
              }

              return (
                <Box
                  mt={2}
                  p={2}
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 4,
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    CÓMO SE REPARTE EL MOVIMIENTO
                  </Typography>

                  {filaDesglose(
                    "deposito",
                    "",
                    "Depósito recibido",
                    movimiento.monto,
                  )}

                  {/* Sin retenciones el depósito ya es el total a repartir, y
                      repetirlo en dos filas iguales solo confunde. */}
                  {hayRetencion && (
                    <>
                      {filaDesglose(
                        "retenido",
                        "+",
                        `${etiquetaNaturaleza(
                          NATURALEZA_LINEA.SUSTRACTIVA,
                        )} · ${detalleGrupo(reparto.lineasSustractivas)}`,
                        reparto.sumaSustractivas,
                      )}
                      <Divider style={{ margin: "6px 0" }} />
                      {filaDesglose(
                        "total",
                        "=",
                        "Total a repartir",
                        reparto.totalARepartir,
                      )}
                    </>
                  )}

                  {filasFacturas.map((fila) =>
                    filaDesglose(fila.clave, "−", fila.etiqueta, fila.monto),
                  )}

                  {reparto.sumaAditivas > 0 &&
                    filaDesglose(
                      "aditivas",
                      "−",
                      `${etiquetaNaturaleza(
                        NATURALEZA_LINEA.ADITIVA,
                      )} · ${detalleGrupo(reparto.lineasAditivas)}`,
                      reparto.sumaAditivas,
                    )}

                  <Divider style={{ margin: "6px 0" }} />
                  {cierre}
                </Box>
              );
            })()}
          </Box>
        )}

        {/* Alert específico para pago insuficiente con preview de distribución */}
        {validacion.valido &&
          diferencia < 0 &&
          categoriasAjuste.length === 0 && (
            <Alert
              severity="info"
              icon={<InfoIcon />}
              style={{ marginTop: 16 }}
            >
              <Typography variant="body2" gutterBottom>
                <strong>ℹ️ Pago insuficiente:</strong> El monto no cubre todas
                las facturas seleccionadas
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>
                  📋 DISTRIBUCIÓN AUTOMÁTICA (por antigüedad de folio):
                </strong>
              </Typography>
              <Typography
                variant="caption"
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontStyle: "italic",
                }}
              >
                Los montos mostrados abajo reflejan exactamente lo que se
                procesará:
              </Typography>
              <Box ml={2}>
                {(() => {
                  // Calcular preview de distribución
                  const facturasOrdenadas = [...facturasSeleccionadas].sort(
                    (a, b) => parseInt(a.folio) - parseInt(b.folio),
                  );

                  let montoRestante = movimiento.monto;

                  return facturasOrdenadas.map((factura) => {
                    const facturaKey = `${factura.tipo}-${factura.id}`;
                    const montoAsignadoActual = parseFloat(
                      montosAsignados[facturaKey] || 0,
                    );

                    let status = "";
                    let color = "";
                    let montoFinal = montoAsignadoActual;

                    if (montoRestante >= montoAsignadoActual) {
                      // Pago completo
                      status = "✅ PAGADA COMPLETA";
                      color = "#2E7D32";
                      montoRestante -= montoAsignadoActual;
                    } else if (montoRestante > 0) {
                      // Pago parcial
                      status = "💰 ABONO PARCIAL";
                      color = "#F57C00";
                      montoFinal = montoRestante;
                      montoRestante = 0;
                    } else {
                      // No se procesa
                      status = "⏸️ NO SE PROCESARÁ";
                      color = "#9E9E9E";
                      montoFinal = 0;
                    }

                    return (
                      <Typography
                        key={facturaKey}
                        variant="caption"
                        component="div"
                        style={{ color, marginBottom: 4 }}
                      >
                        • Factura #{factura.folio} (
                        {formatearMonto(factura.deuda_restante)}): {status}
                        <strong> → {formatearMonto(montoFinal)}</strong>
                        {montoFinal < factura.deuda_restante &&
                          montoFinal > 0 && (
                            <span>
                              {" "}
                              de {formatearMonto(factura.deuda_restante)}
                            </span>
                          )}
                      </Typography>
                    );
                  });
                })()}
              </Box>
              <Typography
                variant="caption"
                style={{ marginTop: 8, display: "block", fontStyle: "italic" }}
              >
                <strong>Nota:</strong> Solo se procesarán las facturas con monto
                asignado. Las facturas restantes no serán incluidas en esta
                conciliación.
              </Typography>
            </Alert>
          )}

        {diferencia === 0 && (
          <Alert severity="success" style={{ marginTop: 16 }}>
            <Typography variant="body2">
              <strong>Perfecto:</strong> Los montos coinciden exactamente.
            </Typography>
          </Alert>
        )}

        {/* Detalle de facturas seleccionadas con montos editables */}
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            MONTOS ASIGNADOS POR FACTURA:
          </Typography>
          <Box mt={1}>
            {facturasSeleccionadas.map((factura) => {
              const facturaKey = `${factura.tipo}-${factura.id}`;
              const montoAsignado = montosAsignados[facturaKey] || 0;
              const porcentajeRaw =
                (montoAsignado / factura.deuda_restante) * 100;
              const porcentaje =
                montoAsignado >= factura.deuda_restante
                  ? "100"
                  : (Math.floor(Math.min(porcentajeRaw, 99.9) * 10) / 10)
                      .toFixed(1)
                      .replace(/\.0$/, "");

              return (
                <Card
                  key={facturaKey}
                  variant="outlined"
                  style={{ marginBottom: 8, padding: 12 }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={factura.tipo}
                          size="small"
                          style={{
                            backgroundColor:
                              factura.tipo === "PLAN" ? "#E3F2FD" : "#FFF3E0",
                            color:
                              factura.tipo === "PLAN" ? "#1976D2" : "#F57C00",
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          #{factura.folio}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleSelectFactura(factura)}
                          style={{ marginLeft: "auto" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Deuda: {formatearMonto(factura.deuda_restante)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Monto a asignar"
                        type="number"
                        value={montoAsignado}
                        onChange={(e) =>
                          handleChangeMonto(factura, e.target.value)
                        }
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Typography
                              variant="body2"
                              style={{ marginRight: 4 }}
                            >
                              $
                            </Typography>
                          ),
                        }}
                        inputProps={{
                          min: 0,
                          max: factura.deuda_restante,
                          step: 1,
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {formatearMonto(montoAsignado)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {porcentaje}% de la deuda
                        </Typography>
                        {/* Indicador si fue ajustado automáticamente */}
                        {usuarioDecidio &&
                          categoriasAjuste.length === 0 &&
                          montoAsignado < factura.deuda_restante && (
                            <Typography
                              variant="caption"
                              style={{
                                display: "block",
                                color: "#F57C00",
                                fontStyle: "italic",
                              }}
                            >
                              🔄 Ajustado automáticamente
                            </Typography>
                          )}
                      </Box>
                    </Grid>
                    {(() => {
                      // Calcular orden de pago para este preview
                      const facturasOrdenadas = [...facturasSeleccionadas].sort(
                        (a, b) => parseInt(a.folio) - parseInt(b.folio),
                      );

                      const sumaTotal = Object.values(montosAsignados).reduce(
                        (sum, monto) => sum + parseFloat(monto || 0),
                        0,
                      );

                      if (categoriasAjuste.length > 0 || !usuarioDecidio) {
                        if (montoAsignado >= factura.deuda_restante) {
                          return (
                            <Chip
                              size="small"
                              label="Pago completo"
                              style={{
                                backgroundColor: "#E8F5E9",
                                color: "#2E7D32",
                                marginLeft: 8,
                                fontSize: "0.7rem",
                              }}
                            />
                          );
                        } else if (montoAsignado > 0) {
                          return (
                            <Chip
                              size="small"
                              label="Pago parcial"
                              style={{
                                backgroundColor: "#FFF3E0",
                                color: "#F57C00",
                                marginLeft: 8,
                                fontSize: "0.7rem",
                              }}
                            />
                          );
                        }
                        return null;
                      }

                      const hayInsuficiencia = sumaTotal > movimiento.monto;

                      if (!hayInsuficiencia) return null;

                      // Determinar posición de esta factura
                      const posicion = facturasOrdenadas.findIndex(
                        (f) => f.tipo === factura.tipo && f.id === factura.id,
                      );

                      let montoAcumulado = 0;
                      for (let i = 0; i < posicion; i++) {
                        const key = `${facturasOrdenadas[i].tipo}-${facturasOrdenadas[i].id}`;
                        montoAcumulado += parseFloat(montosAsignados[key] || 0);
                      }

                      const montoRestante = movimiento.monto - montoAcumulado;

                      if (montoRestante >= montoAsignado) {
                        return (
                          <Chip
                            size="small"
                            label="Pago completo"
                            style={{
                              backgroundColor: "#E8F5E9",
                              color: "#2E7D32",
                              marginLeft: 8,
                              fontSize: "0.7rem",
                            }}
                          />
                        );
                      } else if (montoRestante > 0) {
                        return (
                          <Chip
                            size="small"
                            label="Pago parcial"
                            style={{
                              backgroundColor: "#FFF3E0",
                              color: "#F57C00",
                              marginLeft: 8,
                              fontSize: "0.7rem",
                            }}
                          />
                        );
                      } else {
                        return (
                          <Chip
                            size="small"
                            label="No se procesará"
                            style={{
                              backgroundColor: "#EEEEEE",
                              color: "#757575",
                              marginLeft: 8,
                              fontSize: "0.7rem",
                            }}
                          />
                        );
                      }
                    })()}
                  </Grid>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      {/* HEADER */}
      <DialogTitle className={classes.dialogTitle}>
        Conciliación Manual
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando || isConciliando}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent>
        {/* Información del movimiento */}
        {renderInfoMovimiento()}

        <Box className={classes.section}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  ¿Este movimiento NO tiene facturas asociadas?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Activa esta opción si el movimiento completo corresponde a una
                  retención (contribuciones, impuestos, AFP, etc.) sin pagar
                  ninguna factura.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={modoRetencionCompleta}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setModoRetencionCompleta(isChecked);

                      if (isChecked) {
                        // Retención completa: sin facturas, 1 categoría por
                        // el monto total del movimiento
                        setFacturasSeleccionadas([]);
                        setMontosAsignados({});
                        setUsuarioDecidio(true);
                        setCategoriasAjuste([
                          {
                            uiId: `cat-retencion-${Date.now()}`,
                            categoria_contable_id: "",
                            monto: movimiento ? movimiento.monto : 0,
                            naturaleza: NATURALEZA_LINEA.ADITIVA,
                            observaciones: "",
                          },
                        ]);
                      } else {
                        setCategoriasAjuste([]);
                        setUsuarioDecidio(false);
                      }
                    }}
                    color="primary"
                    disabled={procesando || isConciliando}
                  />
                }
                label="Retención Completa"
                labelPlacement="start"
              />
            </Box>
          </Alert>
        </Box>

        {/* Filtros de búsqueda */}
        {!modoRetencionCompleta && (
          <Box className={classes.section}>
            <Box
              className={classes.filtrosHeader}
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
            >
              <Box display="flex" alignItems="center">
                <FilterListIcon style={{ marginRight: 8 }} />
                <Typography variant="subtitle2">Filtros de Búsqueda</Typography>
                {contarFiltrosActivos() > 0 && (
                  <Chip
                    label={contarFiltrosActivos()}
                    size="small"
                    color="primary"
                    style={{ marginLeft: 8 }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary">
                {mostrarFiltros ? "Ocultar" : "Mostrar"}
              </Typography>
            </Box>

            <Collapse in={mostrarFiltros}>
              <Box className={classes.filtrosContent}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <Grid container spacing={2}>
                    {/* RUT Cliente */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="RUT Cliente"
                        placeholder="76.469.207-1"
                        value={filtros.rut_cliente}
                        onChange={(e) =>
                          handleChangeFiltro("rut_cliente", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>

                    {/* Folio */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Folio"
                        placeholder="12345"
                        value={filtros.folio}
                        onChange={(e) =>
                          handleChangeFiltro("folio", e.target.value)
                        }
                        variant="outlined"
                        inputProps={{ onWheel: (e) => e.target.blur() }}
                        type="number"
                      />
                    </Grid>

                    {/* Estado */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small" variant="outlined">
                        <InputLabel>Estado</InputLabel>
                        <Select
                          value={filtros.estado}
                          onChange={(e) =>
                            handleChangeFiltro("estado", e.target.value)
                          }
                          label="Estado"
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          <MenuItem value="FACTURADO">Facturado</MenuItem>
                          <MenuItem value="ABONO">Abono</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Tipo */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small" variant="outlined">
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={filtros.tipo}
                          onChange={(e) =>
                            handleChangeFiltro("tipo", e.target.value)
                          }
                          label="Tipo"
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          <MenuItem value="PLAN">Plan</MenuItem>
                          <MenuItem value="TAREA">Tarea</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Fecha Desde */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Fecha Desde"
                        value={filtros.fecha_desde}
                        onChange={(newValue) =>
                          handleChangeFiltro("fecha_desde", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            variant="outlined"
                          />
                        )}
                        inputFormat="dd/MM/yyyy"
                      />
                    </Grid>

                    {/* Fecha Hasta */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Fecha Hasta"
                        value={filtros.fecha_hasta}
                        onChange={(newValue) =>
                          handleChangeFiltro("fecha_hasta", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            variant="outlined"
                          />
                        )}
                        inputFormat="dd/MM/yyyy"
                      />
                    </Grid>

                    {/* Monto Mínimo */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Monto Mínimo"
                        placeholder="0"
                        value={filtros.monto_min}
                        onChange={(e) =>
                          handleChangeFiltro("monto_min", e.target.value)
                        }
                        variant="outlined"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <Typography variant="body2">$</Typography>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    </Grid>

                    {/* Monto Máximo */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Monto Máximo"
                        placeholder="999999"
                        value={filtros.monto_max}
                        onChange={(e) =>
                          handleChangeFiltro("monto_max", e.target.value)
                        }
                        variant="outlined"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <Typography variant="body2">$</Typography>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    </Grid>

                    {/* Botones de acción */}
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button
                          variant="outlined"
                          startIcon={<ClearIcon />}
                          onClick={handleLimpiarFiltros}
                          disabled={contarFiltrosActivos() === 0}
                        >
                          Limpiar Filtros
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SearchIcon />}
                          onClick={handleBuscar}
                        >
                          Buscar
                        </Button>
                      </Box>
                    </Grid>

                    {/* Mensaje informativo */}
                    <Grid item xs={12}>
                      <Alert severity="info" icon={false}>
                        <Typography variant="caption">
                          <strong>Tip:</strong> Puedes combinar varios filtros
                          para afinar tu búsqueda. Deja los campos vacíos para
                          buscar sin restricciones en ese criterio.
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Tabla de resultados */}
        {!modoRetencionCompleta && (
          <Box className={classes.section}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle2">
                RESULTADOS DE BÚSQUEDA
              </Typography>
              {resultadosFacturas?.data &&
                resultadosFacturas.data.length > 0 && (
                  <Chip
                    label={`${
                      resultadosFacturas.total || resultadosFacturas.data.length
                    } ${
                      (resultadosFacturas.total ||
                        resultadosFacturas.data.length) === 1
                        ? "factura"
                        : "facturas"
                    }`}
                    color="primary"
                    size="small"
                  />
                )}
            </Box>

            {/* Loading */}
            {(isLoadingFacturas || isFetchingFacturas) && (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginLeft: 16 }}
                >
                  Buscando facturas...
                </Typography>
              </Box>
            )}

            {/* Error */}
            {isErrorFacturas && (
              <Alert severity="error">
                Error al buscar facturas. Por favor, intente nuevamente.
              </Alert>
            )}

            {/* Sin resultados */}
            {!isLoadingFacturas &&
              !isFetchingFacturas &&
              !isErrorFacturas &&
              resultadosFacturas &&
              resultadosFacturas.data &&
              resultadosFacturas.data.length === 0 && (
                <Alert severity="info">
                  No se encontraron facturas con los filtros aplicados. Intente
                  ajustar los criterios de búsqueda.
                </Alert>
              )}

            {/* Tabla de resultados */}
            {!isLoadingFacturas &&
              !isFetchingFacturas &&
              !isErrorFacturas &&
              resultadosFacturas &&
              resultadosFacturas.data &&
              resultadosFacturas.data.length > 0 &&
              renderTablaResultados()}
          </Box>
        )}

        {(facturasSeleccionadas.length > 0 || modoRetencionCompleta) &&
          renderResumenSeleccion()}

        <Divider />

        {/* Campo de observaciones */}
        {facturasSeleccionadas.length > 0 && movimiento && (
          <Box className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              OBSERVACIONES (OPCIONAL)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder="Agregue observaciones sobre esta conciliación..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={`${observaciones.length}/500 caracteres`}
            />
          </Box>
        )}
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions>
        <Button onClick={handleClose} disabled={procesando || isConciliando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={botonConciliarDeshabilitado || procesando || isConciliando}
          loading={isConciliando}
          onClick={handleConciliar}
        >
          {modoRetencionCompleta
            ? "Registrar Retención Completa"
            : `Conciliar (${facturasSeleccionadas.length})`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
